import type { RouterOutput } from "@/lib/trpc-client";
import { formatUsd } from "../formatUsd";

export type MySubmission =
  RouterOutput["creatorPortal"]["getMySubmissions"]["submissions"][number];

export type VideoFilter = "yours" | "all" | "live";

/** A key into the `videos` catalogue, translated where it renders. */
export type VideoFilterKey = `filters.${VideoFilter}`;

/**
 * All, then the two narrower slices.
 *
 * The prototype put "Needs you" first and made it the default, on the
 * argument that it is the only urgent slice. That trades a real cost for
 * it: a creator opening their own library is shown a filtered view of it,
 * and an empty one on the common day when nothing is waiting. Opening on
 * everything is what the page is called, and the dot on "Needs you" carries
 * the urgency without hiding the rest.
 *
 * Widest first, then narrower: All, Live, Needs you.
 */
export const VIDEO_FILTERS: { id: VideoFilter; name: VideoFilterKey }[] = [
  { id: "all", name: "filters.all" },
  { id: "live", name: "filters.live" },
  { id: "yours", name: "filters.yours" },
];

/** What the filter opens on. */
export const DEFAULT_VIDEO_FILTER: VideoFilter = "all";

export function matchesFilter(
  submission: VideoActionSource & Pick<MySubmission, "stage">,
  filter: VideoFilter,
  tiktokEnabled: boolean,
): boolean {
  if (filter === "all") return true;
  if (filter === "live") return submission.stage === "LAUNCHED";
  return needsYou(submission, tiktokEnabled);
}

/**
 * Whether this video is waiting on the creator rather than on us.
 *
 * The one question the gallery is sorted around, so it is answered in one
 * place rather than re-derived per tile.
 */
export function needsYou(
  submission: VideoActionSource,
  tiktokEnabled: boolean,
): boolean {
  return videoAction(submission, tiktokEnabled) !== null;
}

type VideoActionSource = Pick<
  MySubmission,
  | "stage"
  | "revisable"
  | "superseded"
  | "launched"
  | "sparkAdCode"
  | "sparkAuthExpiresAt"
>;

/**
 * Only ask for a Spark code when the program accepts one and it is editable,
 * and only once the brand has signed off: NewForm's approval alone would have
 * the creator post a video the brand may still turn down.
 */
export function videoAction(
  submission: VideoActionSource,
  tiktokEnabled: boolean,
): "revision" | "spark" | null {
  if (submission.superseded) return null;
  if (submission.revisable) return "revision";
  if (
    tiktokEnabled &&
    submission.stage === "CLIENT_APPROVED" &&
    !submission.launched &&
    (!submission.sparkAdCode ||
      (submission.sparkAuthExpiresAt !== null &&
        new Date(submission.sparkAuthExpiresAt).getTime() < Date.now()))
  ) {
    return "spark";
  }
  return null;
}

/**
 * The money line on a tile, or null when there is nothing honest to say.
 *
 * Confirmed earnings beat an estimate, and an estimate only appears once the
 * brand has signed off — before that nothing is owed, and a figure the brand
 * can still decline against is a promise we cannot keep. That rule is the
 * shipped page's and it survives the redesign; only where it is shown moved.
 *
 * A `videos` catalogue key and the formatted amount it takes, translated
 * where it renders.
 */
export function moneyLine(submission: MySubmission): {
  key: "money.confirmed" | "money.estimate" | "money.paid";
  amount: string;
} | null {
  if (submission.confirmedPayoutUsd !== null)
    return {
      key: "money.confirmed",
      amount: formatUsd(submission.confirmedPayoutUsd),
    };
  if (submission.payoutEligible && submission.estPayoutUsd !== null)
    return {
      key: "money.estimate",
      amount: formatUsd(submission.estPayoutUsd),
    };
  if (submission.paidUsd > 0)
    return { key: "money.paid", amount: formatUsd(submission.paidUsd) };
  return null;
}

/** `m:ss`, from whatever the video element reported. */
export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
